import { defineStore } from 'pinia'
import axiosInstance from '@/api/axios'

const API_BASE = 'v2/estimate'

export const useEstimateStore = defineStore('estimate', {
  state: () => ({
    // 목록
    quotations: [],
    totalCount: 0,
    loading: false,

    // 상세
    quotation: null,
    detailLoading: false,

    // 검색 (비교용)
    searchResults: [],
    searchLoading: false,
  }),

  actions: {
    // ── 목록 조회 ──
    async fetchQuotations(params = {}) {
      this.loading = true
      try {
        const response = await axiosInstance.get(`${API_BASE}/quotations/`, { params })
        this.quotations = response.data.results || response.data
        this.totalCount = response.data.count || this.quotations.length
      } catch (error) {
        console.error('Failed to fetch quotations:', error)
        throw error
      } finally {
        this.loading = false
      }
    },

    // ── 상세 조회 ──
    async fetchQuotation(id) {
      this.detailLoading = true
      try {
        const response = await axiosInstance.get(`${API_BASE}/quotations/${id}/`)
        this.quotation = response.data
        return response.data
      } catch (error) {
        console.error('Failed to fetch quotation:', error)
        throw error
      } finally {
        this.detailLoading = false
      }
    },

    // ── 생성 ──
    async createQuotation(data) {
      const response = await axiosInstance.post(`${API_BASE}/quotations/`, data)
      return response.data
    },

    // ── 수정 ──
    async updateQuotation(id, data) {
      const response = await axiosInstance.patch(`${API_BASE}/quotations/${id}/`, data)
      this.quotation = response.data
      return response.data
    },

    // ── 삭제 ──
    async deleteQuotation(id) {
      await axiosInstance.delete(`${API_BASE}/quotations/${id}/`)
    },

    // ── 원가 재계산 ──
    async recalculate(id) {
      const response = await axiosInstance.post(`${API_BASE}/quotations/${id}/recalculate/`)
      this.quotation = response.data
      return response.data
    },

    // ── 확정/취소 ──
    async confirm(id) {
      const response = await axiosInstance.post(`${API_BASE}/quotations/${id}/confirm/`)
      this.quotation = response.data
      return response.data
    },

    async unconfirm(id) {
      const response = await axiosInstance.post(`${API_BASE}/quotations/${id}/unconfirm/`)
      this.quotation = response.data
      return response.data
    },

    // ── 사전원가 → 견적서 전환 ──
    async convertToQuotation(id) {
      const response = await axiosInstance.post(`${API_BASE}/quotations/${id}/convert_to_quotation/`)
      return response.data
    },

    // ── 검색 (비교/복사용) ──
    async searchQuotations(params = {}) {
      this.searchLoading = true
      try {
        const response = await axiosInstance.get(`${API_BASE}/quotations/search/`, { params })
        this.searchResults = response.data
        return response.data
      } catch (error) {
        console.error('Failed to search quotations:', error)
        throw error
      } finally {
        this.searchLoading = false
      }
    },

    // ── 자재 CRUD ──
    async addMaterial(data) {
      const response = await axiosInstance.post(`${API_BASE}/quotation-materials/`, data)
      return response.data
    },

    async updateMaterial(id, data) {
      const response = await axiosInstance.patch(`${API_BASE}/quotation-materials/${id}/`, data)
      return response.data
    },

    async deleteMaterial(id) {
      await axiosInstance.delete(`${API_BASE}/quotation-materials/${id}/`)
    },

    async bulkCreateMaterials(items) {
      const response = await axiosInstance.post(`${API_BASE}/quotation-materials/bulk_create/`, { items })
      return response.data
    },

    // ── CMT (upsert) ──
    async saveCMT(data) {
      const response = await axiosInstance.post(`${API_BASE}/quotation-cmt/`, data)
      return response.data
    },

    async updateCMT(id, data) {
      const response = await axiosInstance.patch(`${API_BASE}/quotation-cmt/${id}/`, data)
      return response.data
    },

    // ── 경비 (upsert) ──
    async saveExpense(data) {
      const response = await axiosInstance.post(`${API_BASE}/quotation-expense/`, data)
      return response.data
    },

    async updateExpense(id, data) {
      const response = await axiosInstance.patch(`${API_BASE}/quotation-expense/${id}/`, data)
      return response.data
    },

    // ── 사이즈 배분 ──
    async saveSizeDistributions(quotationId, sizes) {
      const response = await axiosInstance.post(
        `${API_BASE}/quotation-size-distributions/bulk_save/`,
        { quotation: quotationId, sizes }
      )
      return response.data
    },

    async importFromMarker(quotationId, markerId) {
      const response = await axiosInstance.post(
        `${API_BASE}/quotation-size-distributions/import_from_marker/`,
        { quotation: quotationId, marker_id: markerId }
      )
      return response.data
    },

    // ── 자재 사이즈별 소요량 ──
    async saveMaterialSizeUsages(materialId, usages) {
      const response = await axiosInstance.post(
        `${API_BASE}/quotation-material-size-usages/bulk_save/`,
        { material: materialId, usages }
      )
      return response.data
    },

    // ── 요척에서 자재 가져오기 ──
    async importFromYardage(quotationId) {
      const response = await axiosInstance.post(
        `${API_BASE}/quotations/${quotationId}/import_from_yardage/`
      )
      this.quotation = response.data
      return response.data
    },

    // ── 샘플에서 자재 가져오기 ──
    async importSampleMaterials(quotationId, sampleRequestId) {
      const response = await axiosInstance.post(
        `${API_BASE}/quotations/${quotationId}/import_sample_materials/`,
        { sample_request_id: sampleRequestId }
      )
      this.quotation = response.data
      return response.data
    },
  },
})
